import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertCircle,
  BookOpen,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  TrendingDown,
  TrendingUp,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const FILTERS = [
  { id: 'all', label: 'Todos' },
  { id: 'a_devolver', label: 'A devolver' },
  { id: 'a_cobrar', label: 'A cobrar' },
  { id: 'unpaid', label: 'Pendiente' },
  { id: 'partial', label: 'Parcial' },
  { id: 'settled', label: 'Cuadrado' },
  { id: 'no_receipt', label: 'Sin comprobante' },
  { id: 'with_receipt', label: 'Con comprobante' }
];

const OWNER_LABELS = {
  user: 'Usuario',
  provider: 'Proveedor',
  driver: 'Conductor',
  referral: 'Referido',
  system: 'Sistema'
};

function formatCurrency(value) {
  const amount = Number(value || 0);
  return `S/ ${amount.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

function formatSignedCurrency(value) {
  const amount = Number(value || 0);
  const prefix = amount > 0 ? '+' : amount < 0 ? '-' : '';
  return `${prefix}${formatCurrency(Math.abs(amount))}`;
}

function getBalanceBadge(balanceLabel) {
  if (balanceLabel === 'a_devolver') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (balanceLabel === 'a_cobrar') {
    return 'bg-red-50 text-red-700 border-red-200';
  }
  return 'bg-slate-50 text-slate-700 border-slate-200';
}

function balanceLabelText(balanceLabel) {
  if (balanceLabel === 'a_devolver') return 'A devolver';
  if (balanceLabel === 'a_cobrar') return 'A cobrar';
  return 'Cuadrado';
}

function statusText(status) {
  if (status === 'partial') return 'Parcial';
  if (status === 'settled') return 'Cuadrado';
  return 'Pendiente';
}

function LedgerAccounting({ userId, selectedDate }) {
  const [startDate, setStartDate] = useState(selectedDate);
  const [endDate, setEndDate] = useState(selectedDate);
  const [balances, setBalances] = useState([]);
  const [pricingAudit, setPricingAudit] = useState(null);
  const [counts, setCounts] = useState(null);
  const [needsSync, setNeedsSync] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [drawerAccount, setDrawerAccount] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  useEffect(() => {
    setStartDate(selectedDate);
    setEndDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    fetchBalances();
    fetchPricingAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, statusFilter, ownerFilter]);

  async function fetchBalances() {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);
    try {
      const params = {
        admin_id: userId,
        start_date: startDate,
        end_date: endDate
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (ownerFilter !== 'all') params.owner_type = ownerFilter;

      const response = await axios.get(`${API_URL}/api/accounting/ledger/balances`, { params });
      setBalances(response.data.balances || []);
      setCounts(response.data.counts || null);
      setNeedsSync(Boolean(response.data.needs_sync));
    } catch (err) {
      console.error('Error loading ledger balances:', err);
      setError('Error al cargar el ledger contable');
    } finally {
      setLoading(false);
    }
  }

  async function fetchPricingAudit() {
    if (!startDate || !endDate) return;

    setAuditLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/accounting/ledger/delivery-pricing-audit`, {
        params: {
          admin_id: userId,
          start_date: startDate,
          end_date: endDate,
          limit: 80
        }
      });
      setPricingAudit(response.data || null);
    } catch (err) {
      console.error('Error loading delivery pricing audit:', err);
      setPricingAudit(null);
    } finally {
      setAuditLoading(false);
    }
  }

  async function handleSyncLegacyPayments() {
    setSyncing(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/accounting/ledger/sync-legacy-payments`, {
        admin_id: userId,
        start_date: startDate,
        end_date: endDate
      });
      await fetchBalances();
      await fetchPricingAudit();
    } catch (err) {
      console.error('Error syncing legacy payments:', err);
      setError('Error al sincronizar pagos existentes al ledger');
    } finally {
      setSyncing(false);
    }
  }

  async function openDrawer(row) {
    setDrawerAccount(row);
    setReconciliation(null);
    setDrawerLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/ledger/accounts/${row.account.id}/reconciliation`,
        {
          params: {
            admin_id: userId,
            start_date: startDate,
            end_date: endDate
          }
        }
      );
      setReconciliation(response.data.reconciliation);
    } catch (err) {
      console.error('Error loading reconciliation:', err);
      setError('Error al cargar el detalle de reconciliacion');
    } finally {
      setDrawerLoading(false);
    }
  }

  function closeDrawer() {
    setDrawerAccount(null);
    setReconciliation(null);
    setDrawerLoading(false);
  }

  const filteredBalances = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return balances;

    return balances.filter((row) => {
      const text = [
        row.participant_name,
        row.email,
        row.phone_number,
        row.bank_name,
        row.account?.owner_type,
        row.account?.owner_id
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return text.includes(normalized);
    });
  }, [balances, searchTerm]);

  const totals = useMemo(() => {
    return filteredBalances.reduce(
      (acc, row) => {
        acc.toReturn += row.closing_balance_cents > 0 ? row.closing_balance : 0;
        acc.toCharge += row.closing_balance_cents < 0 ? Math.abs(row.closing_balance) : 0;
        acc.net += row.closing_balance;
        return acc;
      },
      { toReturn: 0, toCharge: 0, net: 0 }
    );
  }, [filteredBalances]);

  const auditRows = useMemo(() => {
    const rows = pricingAudit?.rows || [];
    return rows.filter((row) => row.audit_status !== 'ok' && row.audit_status !== 'not_chargeable').slice(0, 12);
  }, [pricingAudit]);

  function auditStatusLabel(status) {
    const labels = {
      ok: 'OK',
      missing_ledger_post: 'Falta ledger',
      amount_mismatch: 'Diferencia',
      legacy_fallback: 'Legacy',
      pricing_waived: 'Tarifa desactivada',
      not_chargeable: 'No cobrable'
    };
    return labels[status] || status;
  }

  function auditStatusClass(status) {
    if (status === 'ok') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (status === 'pricing_waived') return 'bg-slate-50 text-slate-700 border-slate-200';
    if (status === 'legacy_fallback') return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col xl:flex-row xl:items-end gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-montserrat font-bold text-slate-500 uppercase">Desde</span>
              <input
                type="date"
                value={startDate || ''}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat"
              />
            </label>
            <label className="block">
              <span className="text-xs font-montserrat font-bold text-slate-500 uppercase">Hasta</span>
              <input
                type="date"
                value={endDate || ''}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat"
              />
            </label>
          </div>

          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-montserrat font-bold text-emerald-700 uppercase">Total a devolver</span>
                <TrendingUp className="w-4 h-4 text-emerald-700" />
              </div>
              <p className="text-xl font-montserrat font-bold text-emerald-800 mt-1">{formatCurrency(totals.toReturn)}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-montserrat font-bold text-red-700 uppercase">Total a cobrar</span>
                <TrendingDown className="w-4 h-4 text-red-700" />
              </div>
              <p className="text-xl font-montserrat font-bold text-red-800 mt-1">{formatCurrency(totals.toCharge)}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-montserrat font-bold text-slate-600 uppercase">Balance neto</span>
                <BookOpen className="w-4 h-4 text-slate-600" />
              </div>
              <p className="text-xl font-montserrat font-bold text-slate-900 mt-1">{formatSignedCurrency(totals.net)}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => {
                fetchBalances();
                fetchPricingAudit();
              }}
              disabled={loading || auditLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-montserrat font-semibold disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading || auditLoading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            <button
              onClick={handleSyncLegacyPayments}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg font-montserrat font-semibold disabled:opacity-50"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
              Sincronizar
            </button>
          </div>
        </div>

        {counts && (
          <p className="text-xs text-slate-500 font-montserrat mt-3">
            Ledger: {counts.ledger_transactions} transacciones | Pagos existentes del periodo: {counts.legacy_payments}
          </p>
        )}
      </div>

      {needsSync && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="font-montserrat text-sm">
            <p className="font-bold">Hay pagos diarios existentes, pero este periodo aun no tiene ledger.</p>
            <p>Usa "Sincronizar" para crear entradas append-only desde la tabla actual de pagos sin modificar los pagos originales.</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 font-montserrat text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-montserrat font-bold text-slate-800">
              Auditoría de tarifas por entrega
            </h3>
            <p className="text-sm text-slate-500 font-montserrat">
              Compara el precio registrado en la entrega contra lo posteado al ledger.
            </p>
          </div>
          {auditLoading && <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <p className="text-xs font-bold text-slate-500 uppercase font-montserrat">Cobrables</p>
            <p className="text-xl font-bold text-slate-800 font-montserrat">
              {pricingAudit?.summary?.chargeable_deliveries || 0}
            </p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <p className="text-xs font-bold text-emerald-700 uppercase font-montserrat">Esperado</p>
            <p className="text-xl font-bold text-emerald-800 font-montserrat">
              {formatCurrency(pricingAudit?.summary?.expected_total || 0)}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-xs font-bold text-blue-700 uppercase font-montserrat">Ledger</p>
            <p className="text-xl font-bold text-blue-800 font-montserrat">
              {formatCurrency(pricingAudit?.summary?.ledger_total || 0)}
            </p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-xs font-bold text-red-700 uppercase font-montserrat">Faltantes</p>
            <p className="text-xl font-bold text-red-800 font-montserrat">
              {pricingAudit?.summary?.missing_ledger_posts || 0}
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs font-bold text-amber-700 uppercase font-montserrat">Legacy</p>
            <p className="text-xl font-bold text-amber-800 font-montserrat">
              {pricingAudit?.summary?.legacy_fallback_deliveries || 0}
            </p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
            <p className="text-xs font-bold text-purple-700 uppercase font-montserrat">Ajustadas</p>
            <p className="text-xl font-bold text-purple-800 font-montserrat">
              {pricingAudit?.summary?.adjusted_deliveries || 0}
            </p>
          </div>
        </div>

        {auditRows.length > 0 ? (
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold text-slate-600 uppercase font-montserrat">Entrega</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-slate-600 uppercase font-montserrat">Usuario</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-slate-600 uppercase font-montserrat">Servicio</th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-slate-600 uppercase font-montserrat">Esperado</th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-slate-600 uppercase font-montserrat">Ledger</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-slate-600 uppercase font-montserrat">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {auditRows.map((row) => (
                  <tr key={row.delivery_point_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-sm font-montserrat text-slate-700">
                      #{row.delivery_point_id}
                      <div className="text-xs text-slate-400">{row.status}</div>
                    </td>
                    <td className="px-3 py-2 text-sm font-montserrat text-slate-700">
                      {row.user_name || `Usuario ${row.user_id || ''}`}
                      <div className="text-xs text-slate-400">{row.district || 'Sin distrito'}</div>
                    </td>
                    <td className="px-3 py-2 text-sm font-montserrat text-slate-700">
                      {row.service_label || 'Sin snapshot'}
                      {row.addon_labels?.length > 0 && (
                        <div className="text-xs text-slate-400">{row.addon_labels.join(', ')}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-right font-semibold font-montserrat text-slate-700">
                      {row.has_pricing_snapshot ? formatCurrency(row.pricing_total) : 'Legacy'}
                    </td>
                    <td className="px-3 py-2 text-sm text-right font-semibold font-montserrat text-slate-700">
                      {formatCurrency(row.ledger_posted)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex px-2 py-1 rounded-full border text-xs font-bold font-montserrat ${auditStatusClass(row.audit_status)}`}>
                        {auditStatusLabel(row.audit_status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl p-3 text-sm font-montserrat">
            No hay diferencias críticas de pricing/ledger en el periodo seleccionado.
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar cuenta, usuario, telefono, banco..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
            />
          </div>

          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="px-3 py-2.5 border border-slate-300 rounded-lg font-montserrat text-sm"
          >
            <option value="all">Todos los tipos</option>
            <option value="user">Usuarios</option>
            <option value="provider">Proveedores</option>
            <option value="driver">Conductores</option>
            <option value="referral">Referidos</option>
          </select>
        </div>

        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setStatusFilter(filter.id)}
              className={`px-3 py-2 rounded-lg text-sm font-montserrat font-semibold transition-all ${
                statusFilter === filter.id
                  ? 'bg-brand-primary text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      ) : filteredBalances.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-montserrat">No hay cuentas de ledger para los filtros seleccionados.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase font-montserrat">Cuenta</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase font-montserrat">Saldo inicial</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase font-montserrat">A depositar</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase font-montserrat">Cargos</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase font-montserrat">Movimientos</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase font-montserrat">Balance neto</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase font-montserrat">Estado</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase font-montserrat">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredBalances.map((row) => (
                <tr key={row.account.id} className="hover:bg-slate-50">
                  <td className="px-4 py-4">
                    <p className="font-montserrat font-bold text-slate-900">{row.participant_name}</p>
                    <p className="text-xs text-slate-500 font-montserrat">
                      {OWNER_LABELS[row.account.owner_type] || row.account.owner_type} #{row.account.owner_id}
                      {row.has_bank_account ? ' | Cuenta bancaria registrada' : ' | Sin cuenta bancaria'}
                    </p>
                  </td>
                  <td className="px-4 py-4 text-right font-montserrat text-sm text-slate-700">
                    {formatSignedCurrency(row.opening_balance)}
                  </td>
                  <td className="px-4 py-4 text-right font-montserrat text-sm font-semibold text-emerald-700">
                    {formatCurrency(row.deposit_due)}
                  </td>
                  <td className="px-4 py-4 text-right font-montserrat text-sm font-semibold text-red-700">
                    {formatCurrency(row.service_fee_due)}
                  </td>
                  <td className="px-4 py-4 text-right font-montserrat text-xs text-slate-700">
                    <div className="space-y-1">
                      <p className="font-semibold text-emerald-700">FM->U {formatCurrency(row.deposit_recorded)}</p>
                      <p className="font-semibold text-blue-700">U->FM {formatCurrency(row.participant_payment_received)}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right font-montserrat text-sm font-bold">
                    <span className={row.closing_balance_cents >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                      {formatSignedCurrency(row.closing_balance)}
                    </span>
                    {row.effective_balance_source === 'payment_settlement_overlay' &&
                      row.posted_closing_balance_cents !== row.closing_balance_cents && (
                        <p className="text-[10px] text-slate-400 font-semibold mt-1">
                          Ledger publicado: {formatSignedCurrency(row.posted_closing_balance)}
                        </p>
                      )}
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="space-y-1">
                      <span className={`inline-flex px-2 py-1 border rounded-full text-xs font-montserrat font-bold ${getBalanceBadge(row.balance_label)}`}>
                        {balanceLabelText(row.balance_label)}
                      </span>
                      <p className="text-xs text-slate-500 font-montserrat">{statusText(row.payment_status)}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => openDrawer(row)}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-all font-montserrat text-sm font-semibold"
                    >
                      <Eye className="w-4 h-4" />
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawerAccount && (
        <div className="fixed inset-0 z-50 flex">
          <button className="flex-1 bg-black/40" onClick={closeDrawer} title="Cerrar" />
          <div className="w-full max-w-2xl bg-white h-full shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-montserrat font-bold text-slate-500 uppercase">Reconciliacion</p>
                <h3 className="text-lg font-montserrat font-bold text-slate-900">{drawerAccount.participant_name}</h3>
              </div>
              <button onClick={closeDrawer} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {drawerLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
              ) : reconciliation ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <p className="text-xs text-slate-500 font-bold font-montserrat uppercase">Inicial</p>
                      <p className="font-montserrat font-bold text-slate-900 mt-1">{formatSignedCurrency(reconciliation.opening_balance)}</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <p className="text-xs text-slate-500 font-bold font-montserrat uppercase">Periodo</p>
                      <p className="font-montserrat font-bold text-slate-900 mt-1">{formatSignedCurrency(reconciliation.period_balance)}</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <p className="text-xs text-slate-500 font-bold font-montserrat uppercase">Final</p>
                      <p className={`font-montserrat font-bold mt-1 ${reconciliation.closing_balance_cents >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {formatSignedCurrency(reconciliation.closing_balance)}
                      </p>
                      {reconciliation.effective_balance_source === 'payment_settlement_overlay' &&
                        reconciliation.posted_closing_balance_cents !== reconciliation.closing_balance_cents && (
                          <p className="text-[10px] text-slate-400 font-semibold mt-1">
                            Ledger publicado: {formatSignedCurrency(reconciliation.posted_closing_balance)}
                          </p>
                        )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-montserrat font-bold text-slate-900 mb-3">Entradas ledger</h4>
                    <div className="space-y-2">
                      {reconciliation.entries.length === 0 ? (
                        <p className="text-sm text-slate-500 font-montserrat">Sin entradas en este periodo.</p>
                      ) : reconciliation.entries.map((entry) => (
                        <div key={entry.id} className="border border-slate-200 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-montserrat font-semibold text-slate-900">{entry.description || entry.entry_kind}</p>
                              <p className="text-xs text-slate-500 font-montserrat">
                                {entry.accounting_date} | {entry.event_type} | {entry.source_type} #{entry.source_id}
                              </p>
                            </div>
                            <p className={`font-montserrat font-bold ${entry.amount_cents >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                              {formatSignedCurrency(entry.amount)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-montserrat font-bold text-slate-900 mb-3">Movimientos de dinero</h4>
                    <div className="space-y-2">
                      {reconciliation.money_movements.length === 0 ? (
                        <p className="text-sm text-slate-500 font-montserrat">Sin movimientos registrados en este periodo.</p>
                      ) : reconciliation.money_movements.map((movement) => (
                        <div key={movement.id} className="border border-slate-200 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-montserrat font-semibold text-slate-900">
                                {movement.direction === 'fastmax_to_participant' ? 'Fast Max -> participante' : 'Participante -> Fast Max'}
                              </p>
                              <p className="text-xs text-slate-500 font-montserrat">
                                {movement.movement_date} | {movement.method || 'metodo no registrado'} | {movement.reference_code || 'sin referencia'}
                              </p>
                              {movement.receipts.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {movement.receipts.map((receipt) => (
                                    <a
                                      key={receipt.id}
                                      href={receipt.image_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs bg-brand-primary/10 text-brand-primary px-2 py-1 rounded-full font-montserrat font-semibold"
                                    >
                                      Comprobante #{receipt.id}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className="font-montserrat font-bold text-slate-900">{formatCurrency(movement.amount)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 font-montserrat">
                  No se pudo cargar la reconciliacion.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LedgerAccounting;
